/* eslint-disable */
import React, { useState } from 'react';
import SelectDropdown from './SelectDropdown';
import StatusFilter from './StatusFilter';
import SearchInput from './SearchInput';
import FilterSidePanel, {
  FilterConfig,
  SelectedFilters,
} from './FilterSidePanel';
import {
  FilterContainer,
  TableLabel,
  StatusFiltersContainer,
  SortContainer,
  SearchContainer,
  ClearFiltersButton,
} from '../HotelsTable/HotelsTable.styled';
import { FilterAndPaginationWrapper } from './Table/Table.styled';
import { ListFilter, ChevronDown } from 'lucide-react';
import styled from 'styled-components';
import { Tooltip } from './Tooltip';

const FilterIconButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const FilterIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FilterActiveBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  background-color: #10b981;
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.1);
    }
  }
`;

export const StatusDropdownWrapper = styled.div`
  position: relative;
`;

export const StatusDropdownButton = styled.button<{ isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--color-primary-600);
  border-radius: 9999px;
  padding: var(--spacing-1) var(--spacing-4);
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  h1 {
    color: white;
    margin: 0;
    font-size: inherit;
  }

  svg {
    color: white;
    transition: transform 0.2s ease;
    transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  }

  &:hover {
    opacity: 0.9;
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const StatusDropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 1000;
  min-width: 150px;
  width : 100%;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  display: ${props => (props.isOpen ? 'block' : 'none')};
  animation: fadeIn 0.15s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-0.5rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const StatusDropdownItem = styled.button<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.625rem 1rem;
  border: none;
  background-color: ${props => (props.isSelected ? '#f3f4f6' : 'transparent')};
  color: ${props => (props.isSelected ? '#9ca3af' : '#374151')};
  font-size: 0.875rem;
  font-weight: ${props => (props.isSelected ? '600' : '400')};
  text-align: left;
  pointer-events : ${props => (props.isSelected ? 'none' : 'auto')};
  transition: background-color 0.15s ease;

  &:first-child {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }

  &:last-child {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }

  &:hover {
    background-color: ${props => (props.isSelected ? '#f3f4f6' : '#f9fafb')};
  }
`;

interface StatusOption {
  value: string;
  label: string;
  count: string | number;
}

interface FilterBarProps {
  tableLabel?: string;
  searchPlaceholder?: string;
  statusOptions: StatusOption[];
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  primarySortKey?: string;
  onPrimarySortChange?: (sortKey: string, sortOrder: 'asc' | 'desc') => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  onEnterSearch: () => void;
  onClearFilters: () => void;
  isSearchLoading: boolean;
  onlyFasttrack?: boolean;
  fastrackCount?: string;
  onFasttrackChange?: (checked: boolean) => void;
  hideFilters?: boolean;
  // Advanced filter props
  showAdvancedFilters?: boolean;
  advancedFilters?: FilterConfig[];
  selectedAdvancedFilters?: SelectedFilters;
  appliedAdvancedFilters?: SelectedFilters; // For showing badge when filters are applied
  onAdvancedFilterChange?: (filterId: string, value: string) => void;
  onApplyAdvancedFilters?: () => void;
  onClearAdvancedFilters?: () => void;
  // Status dropdown props
  showStatusDropdown?: boolean;
  statusDropdownOptions?: { label: string; value: string }[];
  selectedStatusDropdown?: string;
  onStatusDropdownChange?: (value: string) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  tableLabel = 'Hotels',
  searchPlaceholder = 'Search Hotels...',
  statusOptions,
  selectedStatus,
  onStatusChange,
  primarySortKey,
  onPrimarySortChange,
  searchTerm,
  onSearchChange,
  onClearSearch,
  onEnterSearch,
  onClearFilters,
  isSearchLoading,
  onlyFasttrack,
  fastrackCount,
  onFasttrackChange,
  hideFilters = false,
  showAdvancedFilters = false,
  advancedFilters = [],
  selectedAdvancedFilters = {},
  appliedAdvancedFilters = {},
  onAdvancedFilterChange = () => {},
  onApplyAdvancedFilters = () => {},
  onClearAdvancedFilters = () => {},
  showStatusDropdown = false,
  statusDropdownOptions = [],
  selectedStatusDropdown = '',
  onStatusDropdownChange = () => {},
}) => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);

  const handleToggleFilterPanel = () => {
    setIsFilterPanelOpen(!isFilterPanelOpen);
  };

  const handleCloseFilterPanel = () => {
    setIsFilterPanelOpen(false);
  };

  const handleToggleStatusDropdown = () => {
    setIsStatusDropdownOpen(!isStatusDropdownOpen);
  };

  const handleStatusDropdownSelect = (value: string) => {
    // Don't allow selecting the same item that's already selected
    if (value === selectedStatusDropdown) {
      return;
    }
    onStatusDropdownChange(value);
    setIsStatusDropdownOpen(false);
  };

  const handleClearAdvancedFilter = () => {
    setIsFilterPanelOpen(false);
    onClearAdvancedFilters();
  };

  // Check if any advanced filters are actually applied (after clicking Apply button)
  // Exclude 'status' from advanced filters check since it's now in the dropdown
  // Also exclude default values like 'all' to prevent showing badge for default state
  const hasActiveAdvancedFilters = Object.entries(appliedAdvancedFilters).some(
    ([key, value]) =>
      key !== 'status' && value !== '' && value !== undefined && value !== 'all'
  );

  // Get the display label for the selected status
  const getStatusLabel = () => {
    if (!selectedStatusDropdown || selectedStatusDropdown === '') {
      return tableLabel;
    }
    const selectedOption = statusDropdownOptions.find(
      opt => opt.value === selectedStatusDropdown
    );
    return selectedOption
      ? `${tableLabel} - ${selectedOption.label}`
      : tableLabel;
  };

  // Close status dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isStatusDropdownOpen && !target.closest('[data-status-dropdown]')) {
        setIsStatusDropdownOpen(false);
      }
    };

    if (isStatusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isStatusDropdownOpen]);

  return (
    <>
      <FilterAndPaginationWrapper>
        <FilterContainer>
          <div className="flex gap-4 items-center">
            {/* table label or status dropdown */}
            {showStatusDropdown ? (
              <StatusDropdownWrapper data-status-dropdown>
                <StatusDropdownButton
                  isOpen={isStatusDropdownOpen}
                  onClick={handleToggleStatusDropdown}
                >
                  <h1>{getStatusLabel()}</h1>
                  <ChevronDown size={16} />
                </StatusDropdownButton>
                <StatusDropdownMenu isOpen={isStatusDropdownOpen}>
                  {statusDropdownOptions.map(option => (
                    <StatusDropdownItem
                      key={option.value}
                      isSelected={selectedStatusDropdown === option.value}
                      onClick={() => handleStatusDropdownSelect(option.value)}
                    >
                      {option.label}
                    </StatusDropdownItem>
                  ))}
                </StatusDropdownMenu>
              </StatusDropdownWrapper>
            ) : (
              <TableLabel>
                <h1>{tableLabel}</h1>
              </TableLabel>
            )}
            {/* Status Filters */}
            {!hideFilters && (
              <StatusFiltersContainer>
                <StatusFilter
                  statusOptions={statusOptions}
                  selectedStatus={selectedStatus}
                  onStatusChange={onStatusChange}
                />
              </StatusFiltersContainer>
            )}
          </div>

          <SearchContainer>
            {/* Advanced Filter Icon Button */}
            {showAdvancedFilters && (
              <Tooltip
                content="Advanced Filters"
                position="left"
                icon={
                  <FilterIconButtonWrapper>
                    <FilterIconButton
                      onClick={handleToggleFilterPanel}
                      aria-label="Open filters"
                    >
                      <ListFilter size={20} />
                    </FilterIconButton>
                    {hasActiveAdvancedFilters && <FilterActiveBadge />}
                  </FilterIconButtonWrapper>
                }
              />
            )}

            <SearchInput
              searchTerm={searchTerm}
              placeholder={searchPlaceholder}
              onSearchChange={onSearchChange}
              onClearSearch={onClearSearch}
              onEnterPress={onEnterSearch}
              isSearchLoading={isSearchLoading}
              className="w-72 xl:w-80 2xl:w-96"
            />

            {/* Clear Filters - only show when there's search text or non-ALL status selected or fasttrack is checked or advanced filters are applied */}
            {/* Don't show when only status dropdown is selected */}
            {/* {(searchTerm ||
              (selectedStatus && selectedStatus.toLowerCase() !== 'all') ||
              onlyFasttrack ||
              hasActiveAdvancedFilters) && (
              <ClearFiltersButton onClick={onClearFilters}>
                Clear Filters
              </ClearFiltersButton>
            )} */}
          </SearchContainer>
        </FilterContainer>
      </FilterAndPaginationWrapper>

      {/* Advanced Filter Side Panel */}
      {showAdvancedFilters && (
        <FilterSidePanel
          isOpen={isFilterPanelOpen}
          onClose={handleCloseFilterPanel}
          filters={advancedFilters}
          selectedFilters={selectedAdvancedFilters}
          appliedFilters={appliedAdvancedFilters}
          onFilterChange={onAdvancedFilterChange}
          onApplyFilters={onApplyAdvancedFilters}
          onClearFilters={handleClearAdvancedFilter}
        />
      )}
    </>
  );
};

export default FilterBar;
